import { getSupabaseBrowserClient } from "@/lib/supabase/client";
import { asObject } from "@/lib/utils";
import {
  isNotificationType,
  NOTIFICATION_PAGE_SIZE,
  targetPathForNotification,
  typesForCategory,
  type AppNotification,
  type EmailDigestFrequency,
  type NotificationListFilter,
  type NotificationPreferences,
} from "@/lib/notifications/types";

type NotificationRow = {
  id: string;
  notification_type: string;
  title: string;
  body: string | null;
  actor_id: string | null;
  entity_type: string | null;
  entity_id: string | null;
  target_path: string | null;
  read_at: string | null;
  created_at: string;
};

type PreferenceRow = {
  community_in_app: boolean;
  chat_in_app: boolean;
  service_in_app: boolean;
  system_in_app: boolean;
  community_email: boolean;
  chat_email: boolean;
  service_email: boolean;
  system_email: boolean;
  email_digest: EmailDigestFrequency;
};

function mapNotification(row: NotificationRow): AppNotification | null {
  if (!isNotificationType(row.notification_type)) {
    return null;
  }

  return {
    id: row.id,
    type: row.notification_type,
    title: row.title,
    body: row.body,
    actorId: row.actor_id,
    entityType: row.entity_type,
    entityId: row.entity_id,
    targetPath: targetPathForNotification({
      targetPath: row.target_path,
      type: row.notification_type,
      entityType: row.entity_type,
      entityId: row.entity_id,
    }),
    isRead: Boolean(row.read_at),
    createdAt: row.created_at,
  };
}

function mapPreferences(row: PreferenceRow): NotificationPreferences {
  return {
    communityInApp: row.community_in_app,
    chatInApp: row.chat_in_app,
    serviceInApp: row.service_in_app,
    systemInApp: row.system_in_app,
    communityEmail: row.community_email,
    chatEmail: row.chat_email,
    serviceEmail: row.service_email,
    systemEmail: row.system_email,
    emailDigest: row.email_digest,
  };
}

export async function listNotifications(input: {
  filter: NotificationListFilter;
  before?: string;
  limit?: number;
}) {
  const supabase = getSupabaseBrowserClient();
  const limit = input.limit ?? NOTIFICATION_PAGE_SIZE;
  let query = supabase
    .from("notifications")
    .select(
      "id, notification_type, title, body, actor_id, entity_type, entity_id, target_path, read_at, created_at",
    )
    .order("created_at", { ascending: false })
    .limit(limit);

  if (input.filter === "unread") {
    query = query.is("read_at", null);
  } else if (input.filter !== "all") {
    query = query.in("notification_type", typesForCategory(input.filter));
  }

  if (input.before) {
    query = query.lt("created_at", input.before);
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return (data as NotificationRow[]).flatMap((row) => {
    const mapped = mapNotification(row);
    return mapped ? [mapped] : [];
  });
}

export async function getNotificationById(id: string) {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase
    .from("notifications")
    .select(
      "id, notification_type, title, body, actor_id, entity_type, entity_id, target_path, read_at, created_at",
    )
    .eq("id", id)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data ? mapNotification(data as NotificationRow) : null;
}

export async function getUnreadNotificationCount() {
  const supabase = getSupabaseBrowserClient();
  const { count, error } = await supabase
    .from("notifications")
    .select("id", { count: "exact", head: true })
    .is("read_at", null);

  if (error) {
    throw error;
  }

  return count ?? 0;
}

export async function markNotificationRead(id: string) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase
    .from("notifications")
    .update({ read_at: new Date().toISOString() })
    .eq("id", id)
    .is("read_at", null);

  if (error) {
    throw error;
  }
}

export async function markAllNotificationsRead() {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("mark_all_notifications_read");

  if (error) {
    throw error;
  }
}

export async function getNotificationPreferences() {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("get_my_notification_preferences");

  if (error) {
    throw error;
  }

  const row = asObject(data as PreferenceRow | PreferenceRow[] | null);
  if (!row) {
    throw new Error("Az értesítési beállítások nem olvashatók.");
  }

  return mapPreferences(row);
}

export async function updateNotificationPreferences(
  patch: Partial<NotificationPreferences>,
) {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc(
    "update_my_notification_preferences",
    {
      p_community_in_app: patch.communityInApp ?? null,
      p_chat_in_app: patch.chatInApp ?? null,
      p_service_in_app: patch.serviceInApp ?? null,
      p_system_in_app: patch.systemInApp ?? null,
      p_community_email: patch.communityEmail ?? null,
      p_chat_email: patch.chatEmail ?? null,
      p_service_email: patch.serviceEmail ?? null,
      p_system_email: patch.systemEmail ?? null,
      p_email_digest: patch.emailDigest ?? null,
    },
  );

  if (error) {
    throw error;
  }

  const row = asObject(data as PreferenceRow | PreferenceRow[] | null);
  if (!row) {
    throw new Error("Az értesítési beállítások mentése sikertelen.");
  }

  return mapPreferences(row);
}
